export const MAX_CHAT_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_CHAT_FILE_SIZE_LABEL = "5MB";

export function getAttachmentName(file, fallback = "download") {
  return file?.filename || file?.name || file?.caption || fallback;
}

export function getAttachmentType(file) {
  return file?.filetype || file?.type || "";
}

export function isImageAttachment(file) {
  const type = getAttachmentType(file).toLowerCase();
  const name = getAttachmentName(file, file?.url || "").toLowerCase();

  return (
    type.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)
  );
}

function bufferLikeToBlobPart(value) {
  if (!value) return null;
  if (value instanceof ArrayBuffer) return value;
  if (ArrayBuffer.isView(value)) {
    return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
  }
  if (value?.type === "Buffer" && Array.isArray(value.data)) {
    return new Uint8Array(value.data);
  }
  if (Array.isArray(value)) {
    return new Uint8Array(value);
  }
  return null;
}

export async function attachmentToBlob(file) {
  if (!file) return null;

  const type = getAttachmentType(file) || "application/octet-stream";
  let body = file.body ?? file.originFileObj ?? file.buffer ?? file.data;
  if (body && typeof body.then === "function") {
    body = await body;
  }
  const blobPart = bufferLikeToBlobPart(body);

  if (blobPart) return new Blob([blobPart], { type });
  if (typeof Blob !== "undefined" && body instanceof Blob) return body;
  if (body && typeof body.arrayBuffer === "function") {
    return new Blob([await body.arrayBuffer()], { type });
  }

  return null;
}

export async function createAttachmentObjectUrl(file) {
  if (file?.url) {
    return { url: file.url, revoke: false };
  }

  const blob = await attachmentToBlob(file);
  if (!blob) return { url: null, revoke: false };

  return { url: URL.createObjectURL(blob), revoke: true };
}

export async function downloadAttachment(file, filename) {
  if (!file) return;

  const resolvedName = getAttachmentName(file, filename || "download");
  const localBlob = await attachmentToBlob(file);

  if (localBlob) {
    downloadBlob(localBlob, resolvedName);
    return;
  }

  if (!file.url) return;

  try {
    const response = await fetch(file.url, { mode: "cors", credentials: "omit" });
    if (!response.ok) {
      throw new Error("Failed to fetch the file.");
    }

    const blob = await response.blob();
    downloadBlob(blob, resolvedName);
  } catch {
    const link = document.createElement("a");
    link.href = file.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.download = resolvedName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

function downloadBlob(blob, filename) {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}
